import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import { 
  Translate as TranslateIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { OutputLanguage } from '../services/contentService';

// Language info interface
interface LanguageInfo {
  value: OutputLanguage;
  label: string;
  flag: string;
  description: string;
  isPremium?: boolean;
}

// Language info data
const languageInfoData: LanguageInfo[] = [
  {
    value: OutputLanguage.English,
    label: 'English',
    flag: '🇺🇸',
    description: 'Standard English output.'
  },
  {
    value: OutputLanguage.Hindi,
    label: 'Hindi',
    flag: '🇮🇳',
    description: 'Output in Hindi language.'
  },
  {
    value: OutputLanguage.Hinglish,
    label: 'Hinglish',
    flag: '🇮🇳🇺🇸',
    description: 'Hindi explanation with English technical terms.'
  },
  {
    value: OutputLanguage.Brazilian,
    label: 'Brazilian Portuguese',
    flag: '🇧🇷',
    description: 'Output in Brazilian Portuguese.',
    isPremium: true
  },
  {
    value: OutputLanguage.Brazinglish,
    label: 'Brazinglish',
    flag: '🇧🇷🇺🇸',
    description: 'Brazilian Portuguese explanation with English technical terms.',
    isPremium: true
  },
  {
    value: OutputLanguage.Marathi,
    label: 'Marathi',
    flag: '🇮🇳',
    description: 'Output in Marathi language.',
    isPremium: true
  },
  {
    value: OutputLanguage.Marathinglish,
    label: 'Marathinglish',
    flag: '🇮🇳🇺🇸',
    description: 'Marathi explanation with English technical terms.',
    isPremium: true
  }
];

interface LanguageSelectorProps {
  selectedLanguage: OutputLanguage;
  onLanguageChange: (language: OutputLanguage) => void;
  userSubscription?: 'free' | 'basic' | 'premium';
}

const LanguageSelectorSimple: React.FC<LanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
  userSubscription = 'free'
}) => {
  // Check if language is available for user's subscription
  // For testing purposes, all languages are available
  const isLanguageAvailable = (language: LanguageInfo): boolean => {
    return true; // Make all languages available for testing
  };
  
  // Handle language selection
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const language = event.target.value as OutputLanguage;
    const languageInfo = languageInfoData.find(l => l.value === language);
    
    if (languageInfo && isLanguageAvailable(languageInfo)) {
      onLanguageChange(language);
    }
  };
  
  return (
    <FormControl 
      fullWidth 
      variant="outlined" 
      size="small"
      sx={{ 
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        }
      }}
    >
      <InputLabel id="language-select-label">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TranslateIcon fontSize="small" sx={{ mr: 0.5 }} />
          Language
        </Box>
      </InputLabel>
      
      <Select
        labelId="language-select-label"
        id="language-select"
        value={selectedLanguage}
        onChange={handleLanguageChange}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TranslateIcon fontSize="small" sx={{ mr: 0.5 }} />
            Language
          </Box>
        }
      >
        {languageInfoData.map((language) => {
          const isAvailable = isLanguageAvailable(language);
          
          const menuItem = (
            <MenuItem 
              key={language.value}
              value={language.value} 
              disabled={!isAvailable}
              sx={{
                display: 'flex',
                alignItems: 'center',
                opacity: isAvailable ? 1 : 0.6
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {language.flag}
                </Typography>
                <Typography variant="body2">
                  {language.label}
                </Typography>
                {language.isPremium && !isAvailable && (
                  <LockIcon fontSize="small" sx={{ ml: 'auto', color: 'text.secondary' }} />
                )}
              </Box>
            </MenuItem>
          );
          
          return isAvailable ? (
            menuItem
          ) : (
            <Tooltip 
              key={language.value}
              title="Upgrade to Basic or Premium to unlock this language" 
              arrow
            >
              <span>{menuItem}</span>
            </Tooltip>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default LanguageSelectorSimple;
